/**
 * Documents & emails — one gallery of everything HireOS sends on the
 * Company's behalf. Each row opens its studio: the rendered artifact is
 * injected in-page, block regions (quote sections, email notes) edit in
 * place, and inline scalar values (the quote's defaults) edit as fields
 * beside it — see DESIGN.md "Settings studios".
 */
import { useEffect, useRef, useState } from "react";
import { Banner } from "../../components/ui/Banner";
import { Select } from "../../components/ui/Select";
import {
  getEmailPreview,
  getEmailSettings,
  saveEmailSetting,
  type EmailAudience,
  type EmailSetting,
} from "../../lib/email-settings-api";
import { usePreview } from "./use-preview";
import { JobSheetsSection } from "./JobSheetsSection";
import { QuoteTemplatesSection } from "./QuoteTemplatesSection";
import type { SectionProps } from "./GeneralSection";
import {
  BackLink,
  DrillDownRow,
  Loading,
  SectionHeader,
  SettingsGroup,
} from "./section-chrome";
import chrome from "./settings.module.css";
import styles from "./TemplatesSection.module.css";

const EMAIL_AUDIENCES: {
  audience: EmailAudience;
  title: string;
  description: string;
  backLabel: string;
}[] = [
  {
    audience: "customer",
    title: "Customer emails",
    description: "Quotes, invoices, payments, reminders, and cancellations.",
    backLabel: "All customer emails",
  },
  {
    audience: "contractor",
    title: "Contractor emails",
    description: "Assignment offers, updates, confirmations, and payments.",
    backLabel: "All contractor emails",
  },
  {
    audience: "company",
    title: "Your team's emails",
    description: "New work, approvals, responses, payments, and feedback.",
    backLabel: "All team emails",
  },
];

type StudioView =
  | { kind: "quote" }
  | { kind: "emails"; audience: EmailAudience }
  | { kind: "email"; emailKind: string; audience: EmailAudience }
  | { kind: "jobsheets" }
  | null;

export function TemplatesSection({ settings, onPatch }: SectionProps) {
  const [emails, setEmails] = useState<EmailSetting[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<StudioView>(null);

  useEffect(() => {
    const ac = new AbortController();
    getEmailSettings(ac.signal)
      .then((result) => {
        setEmails(result);
        setError(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => ac.abort();
  }, []);

  if (view?.kind === "quote") {
    return (
      <QuoteTemplatesSection
        settings={settings}
        onPatch={onPatch}
        onBack={() => setView(null)}
      />
    );
  }
  if (view?.kind === "jobsheets") {
    return (
      <JobSheetsSection
        settings={settings}
        onPatch={onPatch}
        onBack={() => setView(null)}
      />
    );
  }
  if (view?.kind === "emails") {
    const group = EMAIL_AUDIENCES.find((g) => g.audience === view.audience);
    if (group) {
      return (
        <section className={chrome.section}>
          <BackLink label={BACK_LABEL} onClick={() => setView(null)} />
          <SectionHeader title={group.title} description={group.description} />
          {error && (
            <p role="alert" className={chrome.sectionError}>
              {error}
            </p>
          )}
          {!emails ? (
            !error && <Loading />
          ) : (
            <ul className={chrome.listBordered}>
              {emails
                .filter((item) => item.audience === view.audience)
                .map((item) => (
                  <DrillDownRow
                    key={item.kind}
                    name={item.name}
                    description={item.description}
                    onOpen={() =>
                      setView({
                        kind: "email",
                        emailKind: item.kind,
                        audience: view.audience,
                      })
                    }
                  />
                ))}
            </ul>
          )}
        </section>
      );
    }
  }
  if (view?.kind === "email") {
    const setting = emails?.find((item) => item.kind === view.emailKind);
    const group = EMAIL_AUDIENCES.find((g) => g.audience === view.audience);
    if (setting) {
      return (
        <EmailStudio
          key={setting.kind}
          setting={setting}
          backLabel={group?.backLabel ?? BACK_LABEL}
          onBack={() => setView({ kind: "emails", audience: view.audience })}
          onSaved={(next) =>
            setEmails(
              (current) =>
                current?.map((item) =>
                  item.kind === next.kind ? next : item,
                ) ?? current,
            )
          }
        />
      );
    }
  }

  return (
    <section className={chrome.section}>
      <SectionHeader
        title="Documents & emails"
        description="Everything HireOS sends on your behalf. Open one to change it beside a live preview."
      />

      {error && (
        <p role="alert" className={chrome.sectionError}>
          {error}
        </p>
      )}

      <div className={chrome.sectionGroups}>
        <SettingsGroup
          id="templates-documents-heading"
          title="Documents"
          description="The PDFs attached to your quotes and Assignments."
        >
          <ul className={chrome.listBordered}>
            <DrillDownRow
              name="Quote"
              description="The priced quote customers receive and approve, and its deposit and balance invoices."
              onOpen={() => setView({ kind: "quote" })}
            />
            {/* Job sheets open their own space: the template list, each drilling
                into its own section editor (#188). */}
            <DrillDownRow
              name="Job sheets"
              description="The documents Contractors get with their Assignments."
              onOpen={() => setView({ kind: "jobsheets" })}
            />
          </ul>
        </SettingsGroup>

        {/* Each audience opens its own space listing that group's emails —
            the gallery stays a short list of destinations, like the document
            rows above. */}
        <SettingsGroup
          id="templates-emails-heading"
          title="Emails"
          description="The emails HireOS sends to customers, Contractors, and your team."
        >
          <ul className={chrome.listBordered}>
            {EMAIL_AUDIENCES.map((group) => (
              <DrillDownRow
                key={group.audience}
                name={group.title}
                description={group.description}
                onOpen={() =>
                  setView({ kind: "emails", audience: group.audience })
                }
              />
            ))}
          </ul>
        </SettingsGroup>
      </div>
    </section>
  );
}

const BACK_LABEL = "All documents & emails";

/* ─── Emails ────────────────────────────────────────────────────────────── */

const NOTE_MAX_LENGTH = 2000;

/** contenteditable text, newline-preserving where the DOM supports it. */
function noteText(el: Element | null): string {
  if (!el) return "";
  const withInner = el as HTMLElement & { innerText?: string };
  const raw =
    typeof withInner.innerText === "string"
      ? withInner.innerText
      : (el.textContent ?? "");
  return raw.trim().slice(0, NOTE_MAX_LENGTH);
}

/**
 * The email is the editor: the rendered email is injected in-page and the
 * opening/closing note regions (server-marked data-note wrappers) are edited
 * in place — leaving a region commits both notes, exactly like the old
 * textareas' blur commit. Only the variant select stays as a control.
 */
function EmailStudio({
  setting,
  backLabel,
  onBack,
  onSaved,
}: {
  setting: EmailSetting;
  backLabel: string;
  onBack: () => void;
  onSaved: (setting: EmailSetting) => void;
}) {
  const variants = setting.variants ?? [];
  const [variant, setVariant] = useState(variants[0]?.key ?? "");
  const [saveError, setSaveError] = useState<string | null>(null);
  // Bumped after a failed save to re-render the preview from the saved copy.
  const [restoreNonce, setRestoreNonce] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keyed on SAVED notes only: while a note is being typed, nothing here
  // changes, so no refetch can blow away the in-progress edit.
  const {
    result,
    loading,
    error: previewError,
  } = usePreview(
    JSON.stringify([
      variant,
      setting.opening_note,
      setting.closing_note,
      restoreNonce,
    ]),
    () =>
      getEmailPreview({
        kind: setting.kind,
        variant,
        opening_note: setting.customisable ? setting.opening_note : "",
        closing_note: setting.customisable ? setting.closing_note : "",
        editable: setting.customisable,
      }),
  );

  /** Leaving a note region commits both notes; unchanged never saves. */
  const handleNoteBlur = (e: React.FocusEvent<HTMLElement>) => {
    if (!(e.target as HTMLElement).closest?.("[data-note]")) return;
    const root = containerRef.current;
    if (!root) return;
    const opening = noteText(root.querySelector('[data-note="opening"]'));
    const closing = noteText(root.querySelector('[data-note="closing"]'));
    if (opening === setting.opening_note && closing === setting.closing_note) {
      return;
    }
    void (async () => {
      try {
        onSaved(
          await saveEmailSetting(setting.kind, {
            opening_note: opening,
            closing_note: closing,
          }),
        );
        setSaveError(null);
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : String(err));
        setRestoreNonce((nonce) => nonce + 1);
      }
    })();
  };

  return (
    <section>
      <BackLink label={backLabel} onClick={onBack} />
      <SectionHeader title={setting.name} description={setting.description} />

      <div className={styles.previewBar}>
        <div className={styles.subjectSlot}>
          {result?.subject && (
            <p className={styles.subject}>
              Subject: {result.subject}
            </p>
          )}
        </div>
        {variants.length > 0 && (
          <div className={styles.variantPicker}>
            {/* No visible label: the trigger's aria-label names it (a <label
                htmlFor> does not associate with a button). */}
            <Select
              id={`email-variant-${setting.kind}`}
              ariaLabel="Preview version"
              value={variant}
              onChange={setVariant}
              options={variants.map((item) => ({
                value: item.key,
                label: item.label,
              }))}
            />
          </div>
        )}
      </div>

      {saveError && (
        <Banner variant="error" className={chrome.bannerTight}>
          {saveError}
        </Banner>
      )}
      {previewError && (
        <Banner variant="error" className={chrome.bannerTight}>
          {previewError}
        </Banner>
      )}

      {!setting.customisable && (
        <p className={styles.fixedNote}>
          This HireOS system email has fixed formatting and Company copy.
        </p>
      )}

      {!result && !previewError && (
        <div className={chrome.buildingPreview}>
          Building preview…
        </div>
      )}
      {result?.html && (
        <div className={loading ? chrome.dimmed : undefined}>
          {/* Sanctioned dangerouslySetInnerHTML: the fragment comes from our
              own admin-authenticated email-preview endpoint, which escapes
              every Company- and Customer-supplied value. The rules that mark
              its editable regions are in this file's module, under `.editor`
              — Go cannot know a hashed class, so they are `:global()` below a
              class that is not. */}
          <div
            ref={containerRef}
            className={styles.editor}
            onBlur={handleNoteBlur}
            dangerouslySetInnerHTML={{ __html: result.html }}
          />
        </div>
      )}
    </section>
  );
}
